import { makeAutoObservable, runInAction } from "mobx";
import { globalModel } from "../global/GlobalModel";
import { SpaceApi } from "./SpaceApi";
import { SpaceCustomerServiceApi } from "./customer-service/SpaceCustomerServiceApi";
import { SpaceMessageCenterPollModel } from "./messages/SpaceMessageCenterPollModel";
import {
  buildSpaceHomeCacheScope,
  loadSpaceHomeCache,
  saveSpaceHomeCache,
} from "./SpaceHomeLocalStore";
import {
  normalizeIntelCommentKind,
  type GetHomeDataResult,
  type MembershipProduct,
  type SpaceModuleConfig,
  type SpaceProfile,
  type SpaceTabItem,
  type UserSubscriptionItem,
} from "./SpaceType";
import type { MemberBadge } from "../components/member-badge/MemberBadgeType";

const P = process.env.PUBLIC_URL || "";

function resolveTabIcon(
  slotActive: string | null | undefined,
  slotDeactive: string | null | undefined,
  moduleActive: string | null | undefined,
  moduleDeactive: string | null | undefined,
  fallbackActive: string,
  fallbackDeactive: string
): { active: string; deactive: string } {
  return {
    active: slotActive ?? moduleActive ?? fallbackActive,
    deactive: slotDeactive ?? moduleDeactive ?? fallbackDeactive,
  };
}

function normalizeStartupModule(raw: SpaceModuleConfig): SpaceModuleConfig {
  const homeRaw = raw.homeSubscribeCard;
  const homeSubscribeCard =
    homeRaw && typeof homeRaw === "object"
      ? {
          enabled: !!homeRaw.enabled,
          sortOrder:
            typeof homeRaw.sortOrder === "number" && homeRaw.sortOrder >= 1
              ? homeRaw.sortOrder
              : 1,
        }
      : undefined;
  return {
    ...raw,
    homeSubscribeCard,
    intelCommentKind:
      raw.productKind === "intel"
        ? normalizeIntelCommentKind(raw.intelCommentKind)
        : undefined,
    qaAllowPublic: raw.productKind === "qa" ? raw.qaAllowPublic === true : undefined,
    description: raw.description?.trim() ?? "",
    pricingMode: raw.pricingMode?.trim() ? raw.pricingMode : "retail",
    priceSpecs: Array.isArray(raw.priceSpecs) ? raw.priceSpecs : [],
    agreementMode: raw.agreementMode?.trim() ? raw.agreementMode : "none",
    agreementTemplateId: raw.agreementTemplateId?.trim() ? raw.agreementTemplateId : "",
    pcEntrance: raw.pcEntrance ?? { enabled: false, sortOrder: null },
    mobileEntrances: raw.mobileEntrances ?? {
      diamond: { enabled: false, sortOrder: 1, activeIconUrl: null, deactiveIconUrl: null },
      tabBar: { enabled: false, sortOrder: 1, activeIconUrl: null, deactiveIconUrl: null },
      mine: { enabled: false, sortOrder: 1, activeIconUrl: null, deactiveIconUrl: null },
      messages: { enabled: false, sortOrder: 1, activeIconUrl: null, deactiveIconUrl: null },
    },
  };
}

function mergeModulesWithSubscriptions(
  modules: SpaceModuleConfig[],
  subs: UserSubscriptionItem[] | null
): MembershipProduct[] {
  if (subs == null || subs.length === 0) {
    return modules.map((m) => ({
      ...m,
      userSubscribed: false,
      daysRemaining: null,
      trialTierVisible: true,
      purchaseBlocked: false,
      purchaseBlockHint: null,
      upgradeEligible: false,
      upgradeSourceSpaceProductId: null,
      upgradeSourceDisplayName: null,
    }));
  }
  const byId = new Map(subs.map((s) => [s.spaceProductId, s]));
  return modules.map((m) => {
    const s = byId.get(m.spaceProductId);
    return {
      ...m,
      userSubscribed: s?.userSubscribed ?? false,
      daysRemaining: s?.daysRemaining ?? null,
      subscribedStrategyCount: s?.subscribedStrategyCount ?? null,
      strategyEntitlements: s?.strategyEntitlements ?? null,
      trialTierVisible: s?.trialTierVisible !== false,
      purchaseBlocked: s?.purchaseBlocked ?? false,
      purchaseBlockHint: s?.purchaseBlockHint ?? null,
      upgradeEligible: s?.upgradeEligible ?? false,
      upgradeSourceSpaceProductId: s?.upgradeSourceSpaceProductId ?? null,
      upgradeSourceDisplayName: s?.upgradeSourceDisplayName ?? null,
    };
  });
}

export function buildSpaceMobileTabs(modules: SpaceModuleConfig[]): SpaceTabItem[] {
  const homeIcons = resolveTabIcon(
    null,
    null,
    null,
    null,
    `${P}/icons/tab-home-active.svg`,
    `${P}/icons/tab-home.svg`
  );
  const home: SpaceTabItem = {
    key: "home",
    label: "首页",
    activeIconUrl: homeIcons.active,
    deactiveIconUrl: homeIcons.deactive,
    requiresLogin: false,
    fixed: true,
  };

  const messagesIcons = resolveTabIcon(
    null,
    null,
    null,
    null,
    `${P}/icons/tab-groupchat-active.svg`,
    `${P}/icons/tab-groupchat.svg`
  );
  const messages: SpaceTabItem = {
    key: "messages",
    label: "消息",
    activeIconUrl: messagesIcons.active,
    deactiveIconUrl: messagesIcons.deactive,
    requiresLogin: true,
    fixed: true,
  };

  const dynamic = modules
    .filter((m) => m.mobileEntrances?.tabBar?.enabled)
    .sort(
      (a, b) =>
        (a.mobileEntrances?.tabBar?.sortOrder ?? 99) -
        (b.mobileEntrances?.tabBar?.sortOrder ?? 99)
    )
    .map((m) => {
      const icons = resolveTabIcon(
        m.mobileEntrances?.tabBar?.activeIconUrl,
        m.mobileEntrances?.tabBar?.deactiveIconUrl,
        m.activeIconUrl,
        m.deactiveIconUrl,
        `${P}/icons/tab-home-active.svg`,
        `${P}/icons/tab-home.svg`
      );
      return {
        key: m.spaceProductId,
        label: m.displayName,
        activeIconUrl: icons.active,
        deactiveIconUrl: icons.deactive,
        requiresLogin: true,
        fixed: false,
      } satisfies SpaceTabItem;
    });

  const mineIcons = resolveTabIcon(
    null,
    null,
    null,
    null,
    `${P}/icons/tab-mine-active.svg`,
    `${P}/icons/tab-mine.svg`
  );
  const mine: SpaceTabItem = {
    key: "mine",
    label: "我的",
    activeIconUrl: mineIcons.active,
    deactiveIconUrl: mineIcons.deactive,
    requiresLogin: true,
    fixed: true,
  };

  return [home, messages, ...dynamic, mine];
}

export function isValidSpaceTab(tab: string | undefined, tabs: SpaceTabItem[]): boolean {
  if (!tab) {
    return false;
  }
  return tabs.some((t) => t.key === tab);
}

/** 路由 segment 是否可访问：TabBar 固定项 / 已配置产品（含仅金刚区入口） */
export function isValidSpaceRoute(
  tab: string | undefined,
  tabs: SpaceTabItem[],
  products: MembershipProduct[]
): boolean {
  if (isValidSpaceTab(tab, tabs)) {
    return true;
  }
  const id = tab?.trim();
  if (!id) {
    return false;
  }
  return products.some((p) => p.spaceProductId === id);
}

export class SpaceModel {
  spaceId = "";
  profile: SpaceProfile | null = null;
  modules: SpaceModuleConfig[] = [];
  membershipProducts: MembershipProduct[] = [];
  homeData: GetHomeDataResult | null = null;
  tabs: SpaceTabItem[] = buildSpaceMobileTabs([]);
  favorStatus: 0 | 1 = 0;
  /** 当前登录用户在本空间的在期会员标识 */
  myMemberBadges: MemberBadge[] = [];
  loaded = false;
  loadError: string | null = null;
  /** 消息 Tab 角标未读数（客服 + 群聊合计） */
  messagesUnreadCount = 0;
  /**
   * 本次打开 H5 且未落在消息 Tab 时的提醒红点；
   * 进入消息 Tab 后清除；有未读数字时不展示红点。
   */
  messagesTabVisitDot = false;
  /** 服务专员聊天页对外展示名称 */
  csEntryDisplayName = "服务专员";
  private readonly messagePollModel = new SpaceMessageCenterPollModel();

  constructor() {
    makeAutoObservable(this);
  }

  get tabsForBar(): SpaceTabItem[] {
    const unread = this.messagesUnreadCount;
    const visitDot = this.messagesTabVisitDot;
    return this.tabs.map((tab) => {
      if (tab.key !== "messages") {
        return tab;
      }
      if (unread > 0) {
        return { ...tab, badgeCount: unread };
      }
      if (visitDot) {
        return { ...tab, showDot: true };
      }
      return tab;
    });
  }

  setMessagesUnreadCount(count: number) {
    this.messagesUnreadCount = Math.max(0, Math.min(99, count));
  }

  setMessagesTabVisitDot(on: boolean) {
    this.messagesTabVisitDot = on;
  }

  startMessagesUnreadPoll() {
    if (!this.spaceId) {
      return;
    }
    this.messagePollModel.startPoll(this.spaceId, (_list, total) => {
      runInAction(() => {
        this.setMessagesUnreadCount(total);
      });
    });
  }

  stopMessagesUnreadPoll() {
    this.messagePollModel.stopPoll();
  }

  resetMessagesUnread() {
    this.stopMessagesUnreadPoll();
    this.messagesUnreadCount = 0;
    this.messagePollModel.reset();
  }

  setSpaceId(id: string) {
    this.spaceId = id;
  }

  resetFavorState() {
    this.favorStatus = 0;
    this.myMemberBadges = [];
  }

  async loadFavorState() {
    const id = this.spaceId;
    if (!id || !globalModel.isLoggedIn) {
      runInAction(() => {
        this.favorStatus = 0;
      });
      return;
    }
    const s = await SpaceApi.getSpaceFavorState(id);
    runInAction(() => {
      this.favorStatus = s?.favorStatus === 1 ? 1 : 0;
    });
  }

  async refreshPublicProfile() {
    const id = this.spaceId;
    if (!id) {
      return;
    }
    const profileRes = await SpaceApi.getSpaceProfile(id);
    runInAction(() => {
      if (profileRes) {
        this.profile = profileRes;
      }
    });
  }

  async submitFavor(want: boolean): Promise<boolean> {
    const id = this.spaceId;
    if (!id) {
      return false;
    }
    const ok = await SpaceApi.setSpaceFavor(id, want);
    if (ok) {
      await this.loadFavorState();
      await this.refreshPublicProfile();
      // 关注免费产品依赖关注态判定权益，需立即刷新订阅合并结果
      await this.refreshSubscriptionMerge();
    }
    return ok;
  }

  async refreshSubscriptionMerge() {
    const id = this.spaceId;
    if (!id || this.modules.length === 0) {
      return;
    }
    if (!globalModel.isLoggedIn) {
      runInAction(() => {
        this.myMemberBadges = [];
        this.membershipProducts = mergeModulesWithSubscriptions(this.modules, null);
      });
      return;
    }
    const subRes = await SpaceApi.getSubscriptionDetail(id);
    runInAction(() => {
      if (typeof subRes === "string") {
        this.myMemberBadges = [];
        this.membershipProducts = mergeModulesWithSubscriptions(this.modules, null);
      } else {
        this.myMemberBadges = subRes.myMemberBadges ?? [];
        this.membershipProducts = mergeModulesWithSubscriptions(
          this.modules,
          subRes.items ?? []
        );
      }
    });
  }

  async refreshHomeData() {
    const id = this.spaceId;
    if (!id) {
      return;
    }
    const scope = buildSpaceHomeCacheScope(id, globalModel.currentUser?.id);
    const homeRes = await SpaceApi.getSpaceHomeData(id);
    runInAction(() => {
      if (this.spaceId !== id) {
        return;
      }
      this.homeData = homeRes;
    });
    saveSpaceHomeCache(scope, homeRes);
  }

  async loadConfig() {
    const id = this.spaceId;
    const homeScope = buildSpaceHomeCacheScope(id, globalModel.currentUser?.id);
    const cachedHome = loadSpaceHomeCache(homeScope);
    runInAction(() => {
      this.loaded = false;
      this.loadError = null;
      this.modules = [];
      this.membershipProducts = [];
      this.homeData = cachedHome?.homeData ?? null;
    });
    const [profile, modulesRaw, csEntryConfig] = await Promise.all([
      SpaceApi.getSpaceProfile(id),
      SpaceApi.getStartupConfig(id),
      SpaceCustomerServiceApi.getSpaceCsEntryConfig(id),
    ]);
    const modules = modulesRaw.map(normalizeStartupModule);
    runInAction(() => {
      if (this.spaceId !== id) {
        return;
      }
      this.profile = profile;
      this.modules = modules;
      this.csEntryDisplayName = csEntryConfig.entryDisplayName;
      this.tabs = buildSpaceMobileTabs(modules);
      this.membershipProducts = mergeModulesWithSubscriptions(modules, null);
      this.loaded = true;
      if (!profile) {
        this.loadError = "空间不存在或暂不可访问";
      }
    });
    void this.refreshHomeData();
  }
}
